// Every lazily-loaded route component, in one place.
//
// Split out of `_routes.jsx`, which was past 1000 lines with these 61
// declarations sitting above the routing table. `_routes.jsx` now holds the
// <Switch> and the guards; this file holds what each route resolves to.
//
// Entries whose path is also in `pageChunks` (routeChunks.js) reuse that same
// importer rather than writing their own `import()`: one specifier means the
// background preload and the later navigation share a fetch, because the
// browser's module map dedupes on the specifier. Writing the import twice
// would silently break that.
import { lazyWithRetry } from 'modules/Common/utils/lazyWithRetry';
import { pageChunks } from './routeChunks';

// ---------------------------------------------------------------------------
// Each entry defers its own chunk until the route is actually
// mounted - and because a denied or non-matching route never creates its
// element (RouteAccessGate returns a different child, and Switch renders only
// the first match), React.lazy's importer never runs for a route the user
// cannot reach. Role-scoped deferral therefore costs those users nothing.
//
// See docs/features/react-lazy-loading/.
// ---------------------------------------------------------------------------

// @xyflow/react lives in 31 files and every one of them is under Diagrams/, so
// this single boundary defers the whole flow chunk with no coordination needed
// elsewhere. The editor is a large chunk, which is why the Suspense fallback in
// ContentWrapper is a real branded loader rather than a spinner.
export const DiagramEditorPage = lazyWithRetry(
  () => import('modules/Diagrams/pages/DiagramEditorPage'),
);

// The operator's audit console shares that Diagrams boundary: it mounts the
// grant and thread panels, not the canvas, but it lives under Diagrams/ and
// only an operator holding a diagram ever reaches it.
export const DiagramAuditRedirect = lazyWithRetry(
  () => import('modules/Diagrams/pages/DiagramAuditRedirect'),
);

// @react-pdf is the single largest vendor chunk (1,652 kB raw / 556 kB gzip)
// and it is imported by 36 files across THREE modules: OperatorReports (30),
// CbamStock (5) and PublicCatalog (1). All three have to be deferred together
// or the chunk stays in the initial graph - one surviving eager import is
// enough to hold the whole thing there. If you make any of these eager again,
// you silently give back the entire 556 kB, so change them as a set.
//
// @tiptap (editor chunk, 470 kB raw / 151 kB gzip) rides along free: it lives
// only in Common/richTextEditor and its sole consumers are two OperatorReports
// files, so deferring OperatorReports defers it too.
//
// pdfjs-dist is unaffected - the copy-pdfjs-worker build hook handles it.
export const OperatorReportPage = lazyWithRetry(
  () => import('modules/OperatorReports/pages/OperatorReportPage'),
);
export const PlanVersionPage = lazyWithRetry(
  () => import('modules/OperatorReports/pages/PlanVersionPage'),
);
export const PublicCatalogPage = lazyWithRetry(
  () => import('modules/PublicCatalog/pages/PublicCatalogPage'),
);

// The costs page reads what it renders off its regime, and the resolver throws
// on a name nobody registered — so the ROUTE states which regime this is, and
// the import is what registers it (uk-cbam-cost-breakdown D16). That import now
// lives inside each wrapper module rather than here, so the registration travels
// inside the lazy chunk instead of holding the whole regime component set in the
// initial bundle. See the wrappers for the full reasoning.
export const CbamCostsEuRoute = lazyWithRetry(
  () => import('modules/CbamCosts/pages/CbamCostsEuRoute'),
);
export const CbamCostsUkRoute = lazyWithRetry(
  () => import('modules/UkCosts/pages/CbamCostsUkRoute'),
);
// The UK CBAM cost calculator as a declarant page (uk-cost-calculator-widget phase 4).
export const UkCostCalculatorPage = lazyWithRetry(
  () => import('modules/UkCalculator/pages/UkCostCalculatorPage'),
);
// A Reconciliation Run (docs/features/imports-purchases-reconciliation): under Purchases.
export const ReconciliationRunPage = lazyWithRetry(
  () => import('modules/Reconciliation/pages/ReconciliationRunPage'),
);
// Price risk & hedging, a sub-page of EU Costs (cbam-hedging-page H1).
export const CbamHedgingPage = lazyWithRetry(
  () => import('modules/CbamCosts/pages/CbamHedgingPage'),
);
// Certificates (docs/features/certificate-management): EU only, beside Costs.
export const CbamCertificatesPage = lazyWithRetry(
  () => import('modules/CbamCertificates/pages/CbamCertificatesPage'),
);

// Role-scoped route groups. A declarant never mounts an operator route, so its
// chunk is never fetched — RouteAccessGate returns a denial element instead of
// the child, and Switch only renders the first match, so React.lazy's importer
// never runs for a route the user cannot reach.
//
// First-paint routes are deliberately NOT here: /, /init, /payment, /pricing,
// /upgrade, /trial-expired, the onboarding pair, NotFoundPage and ComingSoonPage
// stay eager, because adding a chunk fetch to the very first render trades a
// bundle saving for a slower landing.

// Declarant-scoped routes.
export const InstallationsPage = lazyWithRetry(pageChunks['/installations']);
export const InstallationDetails = lazyWithRetry(
  () => import('modules/Installations/InstallationDetails'),
);
export const ImportsSearchPage = lazyWithRetry(
  () => import('modules/Imports/pages/ImportsSearchPage'),
);
export const ImportCreatePage = lazyWithRetry(
  () => import('modules/Imports/pages/ImportCreatePage'),
);
export const ImportEditPage = lazyWithRetry(() => import('modules/Imports/pages/ImportEditPage'));
export const SingleImportPage = lazyWithRetry(
  () => import('modules/Imports/pages/SingleImportPage'),
);
export const LineViewPage = lazyWithRetry(() => import('modules/Imports/pages/LineViewPage'));
export const LineEditPage = lazyWithRetry(() => import('modules/Imports/pages/LineEditPage'));
export const PromotePurchasePage = lazyWithRetry(
  () => import('modules/Imports/pages/PromotePurchasePage'),
);
// Purchases (docs/features/purchases): the Imports pages under the purchase kind.
// One module, one specifier — each route picks its own named export from it, so
// the five share a single chunk and a single fetch.
const purchasePages = () => import('modules/Purchases/pages/PurchasePages');
export const PurchasesSearchPage = lazyWithRetry(() =>
  purchasePages().then((m) => ({ default: m.PurchasesSearchPage })),
);
export const PurchaseCreatePage = lazyWithRetry(() =>
  purchasePages().then((m) => ({ default: m.PurchaseCreatePage })),
);
export const PurchaseEditPage = lazyWithRetry(() =>
  purchasePages().then((m) => ({ default: m.PurchaseEditPage })),
);
export const PurchaseLineEditPage = lazyWithRetry(() =>
  purchasePages().then((m) => ({ default: m.PurchaseLineEditPage })),
);
export const PurchaseLineViewPage = lazyWithRetry(() =>
  purchasePages().then((m) => ({ default: m.PurchaseLineViewPage })),
);
export const UkImportsSearchPage = lazyWithRetry(
  () => import('modules/UkImports/pages/UkImportsSearchPage'),
);
export const UkThresholdBreakdownPage = lazyWithRetry(
  () => import('modules/UkImports/pages/UkThresholdBreakdownPage'),
);
export const UkRegistrationSummaryPage = lazyWithRetry(
  () => import('modules/UkImports/pages/UkRegistrationSummaryPage'),
);
export const SuppliersSearchPage = lazyWithRetry(pageChunks['/suppliers']);
export const SupplierDetailsPage = lazyWithRetry(
  () => import('modules/Suppliers/pages/SupplierDetailsPage'),
);
export const SupplierChasePage = lazyWithRetry(
  () => import('modules/Suppliers/pages/SupplierChasePage'),
);
export const CompaniesSearchPage = lazyWithRetry(
  () => import('modules/Companies/pages/CompaniesSearchPage'),
);
export const ReportPage = lazyWithRetry(pageChunks['/reports']);
export const SingleReportPage = lazyWithRetry(
  () => import('modules/Reports/pages/SingleReportPage'),
);
export const DeclarantOverviewPage = lazyWithRetry(pageChunks['/overview']);
export const EmissionsDataPage = lazyWithRetry(pageChunks['/emissions']);
export const CbamQuotesSearchPage = lazyWithRetry(
  () => import('modules/CbamQuote/pages/CbamQuotesSearchPage'),
);
export const CbamQuoteCreatePage = lazyWithRetry(
  () => import('modules/CbamQuote/pages/CbamQuoteCreatePage'),
);
export const BenchmarksSearchPage = lazyWithRetry(
  () => import('modules/Benchmarks/pages/BenchmarksSearchPage'),
);
export const AnalyticsOverviewRoute = lazyWithRetry(pageChunks['/analytics-overview']);
export const UploadHubPage = lazyWithRetry(pageChunks['/upload-hub']);
export const DownloadHubPage = lazyWithRetry(
  () => import('modules/DownloadHub/pages/DownloadHubPage'),
);
export const ErpIntegrationPage = lazyWithRetry(
  () => import('modules/Declarant/pages/ErpIntegrationPage'),
);
export const BatchDetailPage = lazyWithRetry(
  () => import('modules/UploadHub/pages/BatchDetailPage'),
);
export const UploadLogDetailPage = lazyWithRetry(
  () => import('modules/UploadHub/pages/UploadLogDetailPage'),
);

// Operator-scoped routes.
export const OperatorOverviewPage = lazyWithRetry(pageChunks['/operator-overview']);
export const OperatorInstallationsPage = lazyWithRetry(pageChunks['/operator-installations']);
export const OperatorInstallationDetailsPage = lazyWithRetry(
  () => import('modules/OperatorInstallations/pages/OperatorInstallationDetailsPage'),
);
export const OperatorInvitePage = lazyWithRetry(
  () => import('modules/Operator/pages/OperatorInvitePage'),
);
export const OperatorConnectedPage = lazyWithRetry(
  () => import('modules/Operator/pages/OperatorConnectedPage'),
);
export const OperatorAnalyticsPage = lazyWithRetry(
  () => import('modules/OperatorAnalytics/pages/OperatorAnalyticsPage'),
);
export const CbamPremiumsPage = lazyWithRetry(
  () => import('modules/CbamPremiums/pages/CbamPremiumsPage'),
);
export const VerificationPage = lazyWithRetry(
  () => import('modules/Operator/pages/VerificationPage'),
);

// Consultant and multi-org routes.
export const ConsultantOverviewPage = lazyWithRetry(pageChunks['/consultant-overview']);
// Verifier firm (verifier-firm-foundation).
export const VerifierHomePage = lazyWithRetry(pageChunks['/verifier']);
export const EngagementPage = lazyWithRetry(() => import('modules/Verifier/pages/EngagementPage'));
export const VerifierAuditPage = lazyWithRetry(() => import('modules/Audit/pages/AuditPage'));
export const VerifierAuditPlanPage = lazyWithRetry(
  () => import('modules/Audit/pages/AuditPlanPage'),
);
export const VerifierSetupPage = lazyWithRetry(
  () => import('modules/Verifier/pages/VerifierSetupPage'),
);
export const VerifierTeamPage = lazyWithRetry(
  () => import('modules/Verifier/pages/VerifierTeamPage'),
);
export const VerifierFirmProfilePage = lazyWithRetry(
  () => import('modules/Verifier/pages/VerifierFirmProfilePage'),
);
export const CarbonPriceTrackerPage = lazyWithRetry(
  () => import('modules/Analytics/pages/CarbonPriceTrackerPage'),
);
export const CostRadarPage = lazyWithRetry(() => import('modules/CostRadar/pages/CostRadarPage'));

// Admin / permissions routes.
export const TeamManagementPage = lazyWithRetry(
  () => import('modules/PermissionsAdmin/pages/TeamManagementPage'),
);
export const RolesListPage = lazyWithRetry(
  () => import('modules/PermissionsAdmin/pages/RolesListPage'),
);
export const RoleEditorPage = lazyWithRetry(
  () => import('modules/PermissionsAdmin/pages/RoleEditorPage'),
);

// The CbamStock stock/inventory pages - the third @react-pdf owner.
export const StockSuppliersPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockSuppliersPage'),
);
export const StockSupplierDetailPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockSupplierDetailPage'),
);
export const StockPurchaseOrdersPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockPurchaseOrdersPage'),
);
export const StockInstallationDetailPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockInstallationDetailPage'),
);
export const StockInventoryLotsPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockInventoryLotsPage'),
);
export const StockLotBreakdownPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockLotBreakdownPage'),
);
export const StockSalesOrdersPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockSalesOrdersPage'),
);
export const StockUploadHubPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockUploadHubPage'),
);
export const StockDownloadHubPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/StockDownloadHubPage'),
);
export const OperatorReportsPage = lazyWithRetry(
  () => import('modules/CbamStock/pages/OperatorReportsPage'),
);
